<template>
    <div class="wildlife-admin-page">
        <h1>Wildlife Administration</h1>
        
        <div class="admin-actions">
            <router-link to="/wildlifeform" class="new-form-button">
                <i class="fas fa-plus"></i> Use New Wildlife Form
            </router-link>
        </div>

        <!-- Action buttons -->
        <div class="admin-actions">
            <button @click="goToWildlifeForm" class="wildlife-form-button">
                <i class="fas fa-plus"></i> New Wildlife Report
            </button>
        </div>

        <!-- Interface for viewing wildlife sightings in trip groups -->
        <div class="view-wildlife-interface">
            <h2>View Wildlife Sightings</h2>
            <div class="date-picker-container">
                <label>Select Date:</label>
                <DatePicker v-model="selectedDate" @change="handleDateChange" />
            </div>
            <div v-if="trips.length === 0" class="no-trips-message">
                <p>No trips found for the selected date.</p>
            </div>
            <div v-else class="trips-container">
                <div v-for="(trip, tripIndex) in sortedTrips" :key="trip.tripid" class="trip-card">
                    <div class="trip-header" @click="toggleTripCollapse(trip)">
                        <h2>Heli #{{ tripIndex + 1 }} ({{ trip.triptype || '' }}) Sort: {{ trip.sortingindex }}</h2>
                        <span>{{ trip.isCollapsed ? '▼' : '▲' }}</span>
                    </div>
                    <div v-if="!trip.isCollapsed" class="groups-container">
                        <div v-for="(group, groupIndex) in sortedGroups(trip.groups)" :key="group.groupid" class="group-card">
                            <h3>Group #{{ groupIndex + 1 }} 
                                <button class="refresh-button" @click="fetchWildlifeForGroup(selectedDate, group)" title="Refresh wildlife sightings">
                                    <i class="fas fa-sync-alt"></i>
                                </button>
                            </h3>
                            <div class="wildlife-sightings">
                                <h4>Wildlife Sightings:</h4>
                                <table class="wildlife-sightings-table">
                                <thead>
                                    <tr>
                                        <th>Company</th>
                                        <th>Tenure #</th>
                                        <th>Heliski Zones</th>
                                        <th>Date</th>
                                        <th>Surveyor Name</th>
                                        <th>Species</th>
                                        <th>Animal Count</th>
                                        <th>UTM Zone</th>
                                        <th>UTM E</th>
                                        <th>UTM N</th>
                                        <th>Comments</th>
                                        <th>Animal Distance</th>
                                        <th>Aspect of Animal</th>
                                        <th>Elevation</th>
                                        <th>Animal Reaction</th>
                                        <th>Immediate Action</th>
                                        <th>End of Day Action</th>
                                        <th>Inventory Method</th>
                                        <th>Ski Run</th>
                                        <th># of Animal Tracks</th>
                                        <th>Type of Machine</th>
                                        <th>Snowmobile Observed</th>
                                        <th>Snowmobile Track Observed</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="!group.wildlifeSightings || group.wildlifeSightings.length === 0">
                                        <td colspan="19" class="no-sightings">No wildlife sightings found for this group.</td>
                                    </tr>
                                    <tr v-for="(sighting, index) in group.wildlifeSightings" :key="index">
                                        <td>{{ sighting.company_name }}</td>
                                        <td>{{ sighting.tenure_number }}</td>
                                        <td>
                                            <div v-if="sighting.zones && sighting.zones.length > 0" class="zones-list">
                                                <span v-for="(zone, zoneIndex) in sighting.zones" :key="zoneIndex" class="zone-item">
                                                    {{ (zone.zone && zone.zone.zonename) || (zone.zone_name && zone.zone_name !== 'Unknown Zone' ? zone.zone_name : null) || 'Unknown Zone' }}
                                                </span>
                                            </div>
                                            <div v-else>{{ sighting.heliski_zone || getRunName(sighting.runid) }}</div>
                                        </td>
                                        <td>{{ sighting.date }}</td>
                                        <td>{{ sighting.surveyor_name }}</td>
                                        <td>{{ sighting.species }}</td>
                                        <td>{{ sighting.animal_count }}</td>
                                        <td>{{ sighting.utm_zone }}</td>
                                        <td>{{ sighting.utm_e }}</td>
                                        <td>{{ sighting.utm_n }}</td>
                                        <td>{{ sighting.comments }}</td>
                                        <td>{{ sighting.animal_distance }}</td>
                                        <td>{{ sighting.aspect_of_animal }}</td>
                                        <td>{{ getElevationName(sighting.elevation) }}</td>
                                        <td>{{ sighting.animal_reaction }}</td>
                                        <td>{{ sighting.immediate_action }}</td>
                                        <td>{{ sighting.end_of_day_action }}</td>
                                        <td>{{ sighting.inventory_method }}</td>
                                        <td>{{ sighting.ski_run }}</td>
                                        <td>{{ sighting.num_of_animal_tracks }}</td>
                                        <td>{{ sighting.type_of_machine }}</td>
                                        <td>{{ sighting.snowmobile_observed }}</td>
                                        <td>{{ sighting.snowmobile_track_observed }}</td>
                                        <td><button class="delete-button" @click="deleteSighting(sighting.wildlifeid, group)">Delete</button></td>
                                    </tr>
                                    <tr>
                                        <td>Eagle Pass Heliskiing</td>
                                        <td>3410026</td>
                                        <td>
                                            <div class="location-selectors">
                                                <div class="multi-zone-selector">
                                                    <select v-model="selectedZoneForGroup[group.groupid]" class="zone-dropdown">
                                                        <option disabled value="">Select Zone</option>
                                                        <option v-for="zone in zones" :key="zone.id" :value="zone.id">{{ zone.zonename }}</option>
                                                    </select>
                                                    <button @click="addZoneToSighting(group)" class="add-zone-btn" :disabled="!selectedZoneForGroup[group.groupid]">Add Zone</button>
                                                </div>
                                                <div v-if="group.newSighting.selectedZones && group.newSighting.selectedZones.length > 0" class="selected-zones">
                                                    <div v-for="(zone, index) in group.newSighting.selectedZones" :key="index" class="zone-tag">
                                                        <span>{{ zone.name }}</span>
                                                        <button @click="removeZoneFromSighting(group, index)" class="remove-zone-btn">&times;</button>
                                                    </div>
                                                </div>
                                                <select v-model="group.newSighting.runid" v-if="!isNoObservation(group.newSighting.species)">
                                                    <option disabled value="">Select Run</option>
                                                    <option v-for="run in group.runs" :key="run.runid" :value="run.runid">{{ run.runname }}</option>
                                                </select>
                                            </div>
                                        </td>
                                        <td><DatePicker v-model="group.newSighting.date" compact clearable /></td>
                                        <td><input type="text" v-model="group.newSighting.surveyor_name" placeholder="Surveyor Name" /></td>
                                        <td><select v-model="group.newSighting.species">
                                                <option disabled value="">Select Species</option>
                                                <option v-for="species in speciesList" :key="species" :value="species">{{ species }}</option>
                                            </select>
                                        </td>
                                        <td><input type="number" v-model="group.newSighting.animal_count" placeholder="Animal Count" /></td>
                                        <td><input type="text" v-model="group.newSighting.utm_zone" placeholder="UTM Zone" /></td>
                                        <td><input type="text" v-model="group.newSighting.utm_e" placeholder="UTM E (6 digits)" maxlength="6" /></td>
                                        <td><input type="text" v-model="group.newSighting.utm_n" placeholder="UTM N (7 digits)" maxlength="7" /></td>
                                        <td><input type="text" v-model="group.newSighting.comments" placeholder="Comments" /></td>
                                        <td><input type="text" v-model="group.newSighting.animal_distance" placeholder="Animal Distance" /></td>
                                        <td><input type="text" v-model="group.newSighting.aspect_of_animal" placeholder="Aspect of Animal" /></td>
                                        <td>
                                            <select v-model="group.newSighting.elevation">
                                                <option value="" disabled>Select Elevation</option>
                                                <option value="3">Alpine</option>
                                                <option value="2">Treeline</option>
                                                <option value="1">Below Treeline</option>
                                            </select>
                                        </td>
                                        <td><input type="text" v-model="group.newSighting.animal_reaction" placeholder="Animal Reaction" /></td>
                                        <td><input type="text" v-model="group.newSighting.immediate_action" placeholder="Immediate Action" /></td>
                                        <td><input type="text" v-model="group.newSighting.end_of_day_action" placeholder="End of Day Action" /></td>
                                        <td><input type="text" v-model="group.newSighting.inventory_method" placeholder="Inventory Method" /></td>
                                        <td><input type="text" v-model="group.newSighting.ski_run" placeholder="Ski Run" /></td>
                                        <td><input type="number" v-model="group.newSighting.num_of_animal_tracks" placeholder="# of Animal Tracks" /></td>
                                        <td><input type="text" v-model="group.newSighting.type_of_machine" placeholder="Type of Machine" /></td>
                                        <td><input type="checkbox" v-model="group.newSighting.snowmobile_observed" /></td>
                                        <td><input type="checkbox" v-model="group.newSighting.snowmobile_track_observed" /></td>
                                        <td><button class="add-button" @click="addSighting(group)">Add Spotting</button></td>
                                    </tr>
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import TripDataService from '@/services/TripDataService.js';
import WildlifeDataService from '@/services/WildlifeDataService.js';
import RunsZonesDataService from '@/services/RunsZonesDataService.js';
import { getElevationName } from '@/utils/wildlifeUtils.js';
import { todayLocalDateString } from '@/utils/dateHelpers.js';
import DatePicker from '../Shared/DatePicker.vue';

export default {
    name: "WildlifeAdmin",
    components: { DatePicker },
    data() {
        return {
            trips: [],
            zones: [],
            runs: [],
            selectedDate: this.getStoredDate() || todayLocalDateString(),
            selectedZoneForGroup: {}, // Track selected zone for each group
            speciesList: [
                'No Observation', // Added as per requirements
                'Grizzly Bear',
                'Moose',
                'Elk',
                'Deer',
                'Mountain Goat',
                'Bighorn Sheep',
                'Wolverine',
                'Wolf',
                'Cougar',
                'Flying Squirrel' // Added as per requirements
                // Removed: 'Snowmobile', 'Black Bear', 'Fox', 'Lynx' as per requirements
            ],
        };
    },
    computed: {
        sortedTrips() {
            return this.trips.slice().sort((a, b) => a.sortingindex - b.sortingindex || a.tripid - b.tripid);
        },
        sortedGroups() {
            return (groups) => groups.slice().sort((a, b) => a.sortingindex - b.sortingindex || a.groupid - b.groupid);
        },
    },
    methods: {
        goToWildlifeForm() {
            this.$router.push('/wildlife-form');
        },
        getStoredDate() {
            return sessionStorage.getItem('selectedDate');
        },
        storeDate(date) {
            sessionStorage.setItem('selectedDate', date);
        },
        handleDateChange() {
            this.storeDate(this.selectedDate);
            this.fetchTripsByDate();
        },
        fetchTripsByDate() {
            TripDataService.fetchTripsByDate(this.selectedDate)
                .then(response => {
                    // Sort trips by sortingindex and tripid
                    this.trips = response.data.sort((a, b) => a.sortingindex - b.sortingindex || a.tripid - b.tripid);

                    // Sort trip groups by sortingindex and groupid within each trip
                    this.trips.forEach(trip => {
                        trip.groups = trip.groups.sort((a, b) => a.sortingindex - b.sortingindex || a.groupid - b.groupid);
                    });

                    this.trips.forEach(trip => {
                        this.$set(trip, 'isCollapsed', true);
                        trip.groups.forEach(group => {
                            // Set the newSighting object for each group with default values
                            this.$set(group, 'newSighting', {
                                zoneid: '',
                                runid: '',
                                date: this.selectedDate,  // Initialize with selectedDate
                                surveyor_name: '',
                                species: '',
                                animal_count: '',
                                utm_zone: '',
                                utm_e: '',  // Added UTM E field
                                utm_n: '',
                                comments: '',
                                animal_distance: '',
                                aspect_of_animal: '',
                                elevation: '',
                                animal_reaction: '',
                                immediate_action: '',
                                end_of_day_action: '',
                                inventory_method: '',
                                ski_run: '',
                                num_of_animal_tracks: '',
                                type_of_machine: '',
                                snowmobile_observed: false,
                                snowmobile_track_observed: false,
                                spottedtime: '',
                                selectedZones: []  // Initialize selectedZones array
                            });
                            this.fetchWildlifeForGroup(this.selectedDate, group);
                        });
                    });
                })
                .catch(e => {
                    console.log(e);
                });
        },

        fetchWildlifeForGroup(date, group) {
            console.log(`Fetching wildlife for group ${group.groupid} on date ${date}`);
            WildlifeDataService.getByDateAndTripGroup(date, group.groupid)
                .then(response => {
                    console.log(`Wildlife sightings for group ${group.groupid}:`, response.data);
                    // Log zone data for debugging
                    response.data.forEach(sighting => {
                        if (sighting.zones) {
                            console.log(`Sighting ${sighting.wildlifeid} zones:`, sighting.zones);
                        }
                    });
                    this.$set(group, 'wildlifeSightings', response.data);
                })
                .catch(e => {
                    console.log(`Error fetching wildlife for group ${group.groupid}:`, e);
                });
        },
        deleteSighting(wildlifeid, group) {
            WildlifeDataService.delete(wildlifeid)
                .then(() => {
                    group.wildlifeSightings = group.wildlifeSightings.filter(sighting => sighting.wildlifeid !== wildlifeid);
                })
                .catch(e => {
                    console.log(e);
                });
        },
        isNoObservation(species) {
            return species === 'No Observation';
        },
        
        addZoneToSighting(group) {
            if (!this.selectedZoneForGroup[group.groupid]) return;
            
            // Find the selected zone from the zones array
            const selectedZone = this.zones.find(zone => zone.id === this.selectedZoneForGroup[group.groupid]);
            if (!selectedZone) return;
            
            // Initialize selectedZones array if it doesn't exist
            if (!group.newSighting.selectedZones) {
                this.$set(group.newSighting, 'selectedZones', []);
            }
            
            // Check if the zone is already selected
            const isDuplicate = group.newSighting.selectedZones.some(zone => zone.id === selectedZone.id);
            if (isDuplicate) {
                alert("This zone is already selected");
                return;
            }
            
            // If not in "No Observation" mode and already have a zone selected,
            // replace the existing zone instead of adding a new one
            if (!this.isNoObservation(group.newSighting.species) && group.newSighting.selectedZones.length > 0) {
                group.newSighting.selectedZones = [{
                    id: selectedZone.id,
                    name: selectedZone.zonename
                }];
                
                alert("For wildlife observations, only one zone can be selected. Previous zone has been replaced.");
            } else {
                // Add the zone to the selected zones
                group.newSighting.selectedZones.push({
                    id: selectedZone.id,
                    name: selectedZone.zonename
                });
            }
            
            // Fetch runs for the selected zone
            this.fetchRunsByZone(selectedZone.id, group);
            
            // Reset the dropdown
            this.selectedZoneForGroup[group.groupid] = '';
        },
        
        removeZoneFromSighting(group, index) {
            if (!group.newSighting.selectedZones) return;
            
            // Remove the zone at the specified index
            group.newSighting.selectedZones.splice(index, 1);
        },
        
        addSighting(group) {
            // Validate only essential fields (species and date)
            if (!group.newSighting.species) {
                alert("Species is required");
                return;
            }
            
            // Validate that at least one zone is selected
            if (!group.newSighting.selectedZones || group.newSighting.selectedZones.length === 0) {
                alert("At least one zone must be selected");
                return;
            }
            
            // Validate UTM E if provided (must be a 6-digit number)
            if (group.newSighting.utm_e && !/^\d{6}$/.test(group.newSighting.utm_e)) {
                alert("UTM E must be a 6-digit number");
                return;
            }
            
            // Validate UTM N if provided (must be a 7-digit number)
            if (group.newSighting.utm_n && !/^\d{7}$/.test(group.newSighting.utm_n)) {
                alert("UTM N must be a 7-digit number");
                return;
            }
            
            // For wildlife observations (not "No Observation"), validate that a ski run is provided
            if (!this.isNoObservation(group.newSighting.species) && !group.newSighting.ski_run) {
                alert("Ski run is required for wildlife observations");
                return;
            }
            
            const spottedDateTime = this.selectedDate + 'T' + (group.newSighting.spottedtime || '00:00:00');
            
            const newSighting = {
                company_name: 'Eagle Pass Heliskiing',
                tenure_number: '3410026',
                runid: group.newSighting.runid || null, // Make runid optional
                zones: group.newSighting.selectedZones.map(zone => ({
                    id: zone.id,
                    name: zone.name
                })),
                date: group.newSighting.date || this.selectedDate,
                surveyor_name: group.newSighting.surveyor_name,
                species: group.newSighting.species,
                animal_count: group.newSighting.animal_count,
                utm_zone: group.newSighting.utm_zone,
                utm_e: group.newSighting.utm_e, // Include UTM E field
                utm_n: group.newSighting.utm_n,
                comments: group.newSighting.comments || (this.isNoObservation(group.newSighting.species) ? 'No wildlife observed in selected zones' : ''),
                animal_distance: group.newSighting.animal_distance,
                aspect_of_animal: group.newSighting.aspect_of_animal,
                elevation: group.newSighting.elevation,
                animal_reaction: group.newSighting.animal_reaction,
                immediate_action: group.newSighting.immediate_action,
                end_of_day_action: group.newSighting.end_of_day_action,
                inventory_method: group.newSighting.inventory_method,
                ski_run: group.newSighting.ski_run,
                num_of_animal_tracks: group.newSighting.num_of_animal_tracks,
                type_of_machine: group.newSighting.type_of_machine,
                snowmobile_observed: group.newSighting.snowmobile_observed,
                snowmobile_track_observed: group.newSighting.snowmobile_track_observed,
                tripgroupid: group.groupid,
                spottedtime: spottedDateTime
            };

        WildlifeDataService.create(newSighting)
            .then(response => {
                group.wildlifeSightings.push(response.data);
                // Reset the new sighting fields
                group.newSighting = {
                    zoneid: '',
                    runid: '',
                    date: '',
                    surveyor_name: '',
                    species: '',
                    animal_count: '',
                    utm_zone: '',
                    coordinates: '',
                    utm_n: '',
                    comments: '',
                    animal_distance: '',
                    aspect_of_animal: '',
                    elevation: '',
                    animal_reaction: '',
                    immediate_action: '',
                    end_of_day_action: '',
                    inventory_method: '',
                    ski_run: '',
                    num_of_animal_tracks: '',
                    type_of_machine: '',
                    snowmobile_observed: false,
                    snowmobile_track_observed: false,
                    spottedtime: ''
                };
            })
            .catch(e => {
                console.log(e);
            });
        },

        fetchZones() {
            RunsZonesDataService.getAllZones()
                .then(response => {
                    this.zones = response.data;
                })
                .catch(e => {
                    console.log(e);
                });
        },

        fetchAllRuns() {
            RunsZonesDataService.getAllRuns()
                .then(response => {
                    this.runs = response.data;
                })
                .catch(e => {
                    console.log(e);
                });
        },
        fetchRunsByZone(zoneId, group) {
            RunsZonesDataService.getRunsByZone(zoneId)
                .then(response => {
                    this.$set(group, 'runs', response.data);
                })
                .catch(e => {
                    console.log(e);
                });
        },
        getRunName(runid) {
            const run = this.runs.find(run => run.runid === runid);
            return run ? run.runname : 'Unknown Run';
        },
        formatTime(timestamp) {
            if (timestamp) {
                return timestamp.split('T')[1].substring(0, 5);
            }
            return 'N/A';
        },
        toggleTripCollapse(trip) {
            trip.isCollapsed = !trip.isCollapsed;
        },
        getElevationName(elevation) {
            return getElevationName(elevation);
        }
    },
    mounted() {
        this.fetchTripsByDate();
        this.fetchZones();
        this.fetchAllRuns();
    }
};
</script>

<style scoped>
.wildlife-admin-page {
    padding: 20px;
    width: calc(100% - 10px); /* Full width minus the 5px margin on each side */
    max-width: 100%;
    margin: 0 5px; /* Apply 5px margin on the left and right */
    box-sizing: border-box; /* Include padding and border in the element's total width */
}

.admin-actions {
    margin-bottom: 20px;
    display: flex;
    gap: 10px;
}

.wildlife-form-button {
    background-color: #28a745;
    color: white;
    border: none;
    padding: 12px 20px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background-color 0.3s ease;
}

.wildlife-form-button:hover {
    background-color: #218838;
}

.wildlife-form-button i {
    font-size: 14px;
}

.view-wildlife-interface {
    margin-bottom: 20px;
}

.date-picker-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-bottom: 10px;
}

.no-trips-message {
    text-align: center;
    font-size: 1.2em;
    color: #888;
}

.trips-container {
    width: 100%; /* Make sure the trips-container takes the full width */
    margin: 0; /* Remove default margin */
    padding: 0; /* Remove default padding */
}

.trip-card {
    background-color: #f9f9f9;
    border: 1px solid #ddd;
    border-radius: 5px;
    padding: 10px; /* Reduce padding */
}

.trip-card, .group-card {
    padding: 10px; /* Keep the padding small */
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #fff;
}

.trip-header {
    padding: 5px 10px;
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 0.9em;
}

.groups-container {
    width: 100%; /* Ensure groups-container takes the full width */
    margin: 0; /* Remove default margin */
    padding: 0; /* Remove default padding */
}

.group-card {
    padding: 10px; /* Reduce padding */
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #fff;
}

.wildlife-sightings {
    margin-top: 10px;
}

.wildlife-sightings-table {
    width: 100%; /* Ensure the table takes the full width of the container */
    border-collapse: collapse;
    font-size: 0.8em;
    margin-bottom: 10px;
}

.wildlife-sightings-table th,
.wildlife-sightings-table td {
    border: 1px solid #ccc;
    padding: 5px;
    text-align: left;
}

.add-button, .delete-button {
    background-color: green;
    color: white;
    border: none;
    border-radius: 5px;
    padding: 5px 10px;
    font-size: 0.8em;
    cursor: pointer;
}

.delete-button {
    background-color: red;
}

.no-sightings {
    text-align: center;
    color: #888;
    font-style: italic;
    padding: 10px;
}

.refresh-button {
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    font-size: 0.8em;
    cursor: pointer;
    margin-left: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s;
}

.refresh-button:hover {
    background-color: #0056b3;
}

.location-selectors {
    display: flex;
    flex-direction: column;
    font-size: 0.9em; /* Smaller font size */
}

.multi-zone-selector {
    display: flex;
    margin-bottom: 5px;
}

.zone-dropdown {
    flex-grow: 1;
    padding: 5px;
    margin-right: 5px;
}

.add-zone-btn {
    background-color: #4CAF50;
    color: white;
    border: none;
    padding: 5px 10px;
    cursor: pointer;
    border-radius: 3px;
    font-size: 0.8em;
}

.add-zone-btn:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
}

.selected-zones {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 5px;
    margin-bottom: 5px;
}

.zone-tag {
    display: flex;
    align-items: center;
    background-color: #f1f1f1;
    padding: 3px 8px;
    border-radius: 3px;
    font-size: 0.9em;
}

.remove-zone-btn {
    background: none;
    border: none;
    color: #ff0000;
    cursor: pointer;
    font-size: 1.2em;
    margin-left: 5px;
    padding: 0 5px;
}

.zones-list {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
}

.zone-item {
    background-color: #f1f1f1;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 0.9em;
}

</style>
